import React, { useState, useEffect, useRef, useCallback } from "react";
import { Constants } from "@eagerdog/constants";
import { IEventSummary, IShow } from "@eagerdog/interfaces";

import { apiService } from "src/services";
import { toast } from "src/components/Toast/ToastManager";
import Checkbox from "src/components/Checkbox/Checkbox";
import Button from "src/components/Button/Button";
import Chip from "src/components/Chip/Chip";
import SearchDown from "src/components/SearchDown/SearchDown";

import styles from "./ShowSettingsTab.module.scss";

interface IProps {
  event: IEventSummary;
  show: IShow;
  onUpdate(): void;
}

interface ISettingRow {
  id: string;
  label: string;
  description: string;
  value: boolean;
  onChange(val: boolean): void;
}

interface IEventChip {
  handle: string;
  label: string;
}

const LOOKUP_DEBOUNCE_MS = 300;

const ShowSettingsTab: React.FC<IProps> = ({ event, show, onUpdate }) => {
  const isAKCRallyOrObedience =
    event.sanctioning_club === Constants.sanctioning_club.AKC &&
    (show.show_type === Constants.show_type.rally_obedience ||
      show.show_type === Constants.show_type.obedience);

  const isAKCScentwork =
    event.sanctioning_club === Constants.sanctioning_club.AKC &&
    show.show_type === Constants.show_type.scentwork;

  const isAKCConformation =
    event.sanctioning_club === Constants.sanctioning_club.AKC &&
    show.show_type === Constants.show_type.conformation;

  const isUKCConformation =
    event.sanctioning_club === Constants.sanctioning_club.UKC &&
    show.show_type === Constants.show_type.conformation;

  const [requireBreederSireDam, setRequireBreederSireDam] = useState<boolean>(
    show.require_breeder_sire_dam ?? false
  );

  const [isFunMatch, setIsFunMatch] = useState<boolean>(
    show.is_fun_match ?? false
  );

  const [saving, setSaving] = useState(false);

  // ── Associated Events state ──────────────────────────────────────────────
  const [chips, setChips] = useState<IEventChip[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildLabel = (handle: string, title: string, startDate?: string | Date) => {
    const datePart = startDate
      ? ` · ${new Date(startDate).toLocaleDateString()}`
      : "";
    return `${title}${datePart} (${handle})`;
  };

  // Resolve saved handles to display labels on mount
  useEffect(() => {
    const handles = show.associated_event_handles ?? [];
    if (handles.length === 0) return;
    Promise.all(
      handles.map(async (handle) => {
        try {
          const ev = await apiService.getEventSummaryForHandle(handle);
          return { handle, label: buildLabel(handle, ev.title, ev.start_date) };
        } catch {
          return { handle, label: handle };
        }
      })
    ).then(setChips);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runLookup = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setSearchResults([]);
      return;
    }
    try {
      const ev = await apiService.getEventSummaryForHandle(trimmed);
      setSearchResults([buildLabel(trimmed, ev.title, ev.start_date)]);
    } catch {
      setSearchResults([]);
    }
  }, []);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runLookup(value), LOOKUP_DEBOUNCE_MS);
  };

  // Parse the handle back out of the formatted label string "(handle)"
  const handleSelect = (resultLabel: string) => {
    const match = resultLabel.match(/\(([^)]+)\)$/);
    const handle = match ? match[1] : resultLabel.trim();
    if (!chips.find((c) => c.handle === handle)) {
      setChips((prev) => [...prev, { handle, label: resultLabel }]);
    }
    setSearchValue("");
    setSearchResults([]);
  };

  const removeChip = (handle: string) => {
    setChips((prev) => prev.filter((c) => c.handle !== handle));
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData: Partial<IShow> = {
        ...show,
        require_breeder_sire_dam: requireBreederSireDam,
        is_fun_match: isFunMatch,
        ...(isUKCConformation && {
          associated_event_handles: chips.map((c) => c.handle),
        }),
      };
      await apiService.updateShow(event._id, show._id, updateData as IShow);
      toast.show({
        title: "Show Settings",
        content: "Settings saved successfully.",
        duration: 8000,
        type: "success",
      });
      onUpdate();
    } catch {
      toast.show({
        title: "Show Settings",
        content: "Failed to save settings. Please try again.",
        duration: 8000,
        type: "fail",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Checkbox settings ─────────────────────────────────────────────────────
  const settings: ISettingRow[] = [];

  if (isAKCRallyOrObedience) {
    settings.push({
      id: "require_breeder_sire_dam",
      label: "Require Breeder, Sire, and Dam information?",
      description:
        "When enabled, dogs must have Breeder, Sire, and Dam filled in before they can register for this show. Dogs with a PAL or Canine Partner registration are exempt.",
      value: requireBreederSireDam,
      onChange: setRequireBreederSireDam,
    });
  }

  const isAKCRallyObedience =
    event.sanctioning_club === Constants.sanctioning_club.AKC &&
    show.show_type === Constants.show_type.rally_obedience;

  if (isAKCScentwork || isAKCConformation || isAKCRallyObedience) {
    settings.push({
      id: "is_fun_match",
      label: "This is a fun match only",
      description:
        "When enabled, dogs can be entered without a date of birth or AKC registration number. Dogs must still have the AKC sanctioning club selected and their name and breed filled in.",
      value: isFunMatch,
      onChange: setIsFunMatch,
    });
  }

  const hasContent = settings.length > 0 || isUKCConformation;

  if (!hasContent) {
    return (
      <div className={styles.ShowSettingsTab}>
        <p className={styles.empty}>No settings are available for this show type.</p>
      </div>
    );
  }

  return (
    <div className={styles.ShowSettingsTab}>
      {settings.length > 0 && (
        <div className={styles.settingsList}>
          {settings.map((setting) => (
            <div key={setting.id} className={styles.settingRow}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>{setting.label}</span>
                <span className={styles.settingDescription}>{setting.description}</span>
              </div>
              <div className={styles.settingControl}>
                <Checkbox
                  id={setting.id}
                  label=""
                  value={setting.value}
                  onChange={(e) => setting.onChange(e.target.checked)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {isUKCConformation && (
        <div className={styles.associatedEvents}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>Associated Events</span>
            <span className={styles.sectionDescription}>
              Link performance event(s) whose scores should count toward Total Dog
              eligibility alongside this conformation show.
            </span>
          </div>

          <div className={styles.searchRow}>
            <div className={styles.searchWrap}>
              <SearchDown
                label="Search Event Handle"
                placeholder="Type an event handle…"
                defaultValue={searchValue}
                onSearch={handleSearch}
                searchResults={searchResults}
                onSelect={handleSelect}
                onClear={() => {
                  setSearchValue("");
                  setSearchResults([]);
                }}
                showClear={true}
              />
            </div>
          </div>

          <div className={styles.chipList}>
            {chips.map((chip) => (
              <Chip key={chip.handle} onRemove={() => removeChip(chip.handle)}>
                {chip.label}
              </Chip>
            ))}
            {chips.length === 0 && (
              <div className={styles.emptyList}>No associated events linked</div>
            )}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <Button isLoading={saving} onClick={handleSave}>
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default ShowSettingsTab;
